import { Component, HostListener, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Call } from '@twilio/voice-sdk';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { clearsMicrophoneAlarm, raisesMicrophoneAlarm } from 'src/app/models/call-audio-warning.model';
import { CallQuality, INITIAL_CALL_QUALITY } from 'src/app/models/call-quality.model';
import {
  isReportableMicrophoneFailure,
  MICROPHONE_CHECK_MESSAGES,
  MicrophoneCheckResult,
} from 'src/app/models/microphone-check.model';
import { PatientLookupParticipant } from 'src/app/models/patient-lookup.model';
import { User } from 'src/app/models/user.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { ChartDraftCoordinatorService } from 'src/app/services/chart-draft/chart-draft-coordinator.service';
import { CallQualityService } from 'src/app/services/communications/call-quality.service';
import { PatientLookupService } from 'src/app/services/communications/patient-lookup.service';
import { TwilioService } from 'src/app/services/communications/twilio.service';
import { UtilsService } from 'src/app/services/utils.service';

export interface CallInProgressData {
  call: Call;
  callerNumber: string;
  callerName?: string;
  /**
   * Result of the pre-answer microphone check. An incoming call is answered even when the check
   * fails — dropping a clinical transfer is worse — so the verdict travels here to be shown instead.
   */
  microphone?: MicrophoneCheckResult;
  /**
   * A TCM discharge clinical transfer (`callType=discharge_call`). Its patient chart button shows
   * whatever the chart-draft canary says: the RN reached may belong to another clinic, and the
   * patient search only covers their own clinics, so this button is their only way to the chart.
   */
  isPostDischargeCall?: boolean;
}

@Component({
  selector: 'app-call-in-progress-dialog',
  templateUrl: './call-in-progress-dialog.component.html',
  styleUrls: ['./call-in-progress-dialog.component.scss'],
})
export class CallInProgressDialogComponent implements OnInit, OnDestroy {
  duration = 0;
  durationFormatted = '00:00';
  formattedCallerNumber: string;
  isMuted = false;
  /** Set while the care manager's microphone is known not to be reaching the patient. */
  microphoneWarning: string | null = null;
  /** Signal of the browser leg, for the bars next to the timer. */
  callQuality: CallQuality = INITIAL_CALL_QUALITY;
  isOnHold = false;
  holdPending = false;
  isMinimized = true;
  isDragging = false;
  position = { x: 20, y: window.innerHeight - 120 };
  dragStart = { x: 0, y: 0 };
  dialogWidth = 400;
  dialogHeight = 350;
  minimizedWidth = 350;
  minimizedHeight = 80;
  currentUser: User;
  /** Patient resolved from the caller number, if any; enables the "go to chart" action. */
  matchedPatient: PatientLookupParticipant | null = null;
  /** Whether the preserve-work-on-call feature is enabled for this user (canary allowlist). */
  chartDraftEnabled$: Observable<boolean>;
  showAddParticipant = false;
  parentCallSid: string;
  errorMessage: string | null = null;
  additionalParticipantSessionId: string;
  additionalParticipantCallSid: string;
  additionalParticipantNumber: string;
  phoneNumberControl = new UntypedFormControl('', [
    Validators.required,
    (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      const digits = control.value.replace(/\D/g, '');
      const isValid = /^1?[2-9]\d{9}$/.test(digits) || /^1?800\d{7}$/.test(digits);
      return isValid ? null : { invalidPhone: true };
    },
  ]);

  // Private properties
  private originalPositionY = window.innerHeight - 120;
  private keypadPositionY: number | null = null;
  private durationInterval: any;
  private participantDestroy$?: Subject<void>;
  /** Component lifetime, for subscriptions that outlive a single call leg. */
  private readonly destroy$ = new Subject<void>();
  /** Cancels an in-flight hold request; internal, so it lives with the private members. */
  private readonly holdCancel$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CallInProgressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CallInProgressData,
    private utilsService: UtilsService,
    private twilioService: TwilioService,
    private authService: NewAuthService,
    private patientLookupService: PatientLookupService,
    private chartDraftCoordinator: ChartDraftCoordinatorService,
    private callQualityService: CallQualityService,
    private router: Router,
    private zone: NgZone
  ) {
    this.parentCallSid = this.data.call.customParameters.get('parentCallSid');
    this.dialogRef.disableClose = true;
    this.formattedCallerNumber = this.utilsService.formatPhoneNumber(this.data.callerNumber);
    this.chartDraftEnabled$ = this.chartDraftCoordinator.canaryEnabled$;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const newX = event.clientX - this.dragStart.x;
      const newY = event.clientY - this.dragStart.y;

      // Get current dimensions based on minimized state
      const currentWidth = this.isMinimized ? this.minimizedWidth : this.dialogWidth;
      const currentHeight = this.isMinimized ? this.minimizedHeight : this.dialogHeight;

      // Keep within viewport bounds
      const maxX = window.innerWidth - currentWidth - 20;
      const maxY = window.innerHeight - currentHeight - 20;

      this.position.x = Math.max(20, Math.min(newX, maxX));
      this.position.y = Math.max(20, Math.min(newY, maxY));

      this.updateDialogPosition();
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Ensure dialog stays within viewport when window is resized
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const currentWidth = this.isMinimized ? this.minimizedWidth : this.dialogWidth;
    const currentHeight = this.isMinimized ? this.minimizedHeight : this.dialogHeight;

    this.position = {
      x: Math.max(0, Math.min(this.position.x, windowWidth - currentWidth - 20)),
      y: Math.max(0, Math.min(this.position.y, windowHeight - currentHeight - 20)),
    };

    this.updateDialogPosition();
  }

  ngOnInit() {
    this.currentUser = this.authService.user;
    this.startTimer();
    this.resolveCallerPatient();
    this.watchMicrophone();
    if (this.data.call) {
      this.callQualityService
        .watch(this.data.call)
        .pipe(takeUntil(this.destroy$))
        .subscribe((quality) => (this.callQuality = quality));
    }

    // Initialize position at bottom-left after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.initializePosition();
    }, 0);

    // Listen for call disconnect. Back into Angular's zone, as TwilioService does for the incoming
    // dialog: the SDK can fire outside it, and a dialog closed there stays drawn until the next tick.
    if (this.data.call) {
      this.data.call.on('disconnect', () => {
        this.zone.run(() => this.onCallEnded());
      });
    }
  }

  ngOnDestroy() {
    this.stopTimer();
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupParticipantSubscription();
    this.resetHoldState();
    // Only here, never in resetHoldState: that also runs on hang-up, and a completed Subject would
    // make takeUntil short-circuit every later hold request.
    this.holdCancel$.complete();
  }

  onHangUp(): void {
    if (this.data.call) {
      this.data.call.disconnect();
    }
    this.resetHoldState();
    this.dialogRef.close({ action: 'hangup', duration: this.duration });
  }

  /**
   * Navigates to the caller's chart, preserving the current chart's in-progress work and elapsed
   * time in the temporary draft. The floating call dialog survives the navigation
   * (closeOnNavigation is disabled), so the call stays controllable from the new chart.
   */
  goToPatientChart(): void {
    const patientId = this.matchedPatient?.userId;
    if (!patientId) {
      return;
    }
    // With the canary on, the coordinator keeps a draft of the chart she is leaving. Off, it does
    // nothing — so a discharge call navigates on its own rather than leave the button dead.
    if (this.chartDraftCoordinator.isCanaryEnabled) {
      this.chartDraftCoordinator.jumpToPatient(patientId);
    } else if (this.data.isPostDischargeCall) {
      this.router.navigate(['clinical/patient-detail', patientId, 'home']);
    }
  }

  /**
   * Toggles hold on the caller's leg.
   *
   * Unlike mute — which is local to the browser leg and instant — hold is a REST round-trip that
   * may also promote the call to a Twilio conference (~1-2s audio gap the first time). The button
   * therefore stays disabled until the backend confirms, and the visual state only flips on
   * success: a button that lied about hold would leave a patient hearing the room.
   *
   * Declared here, above the first private member, rather than next to toggleMute, so it does not
   * add another @typescript-eslint/member-ordering violation to this file.
   */
  toggleHold(): void {
    if (!this.parentCallSid || this.holdPending) {
      return;
    }

    const nextHold = !this.isOnHold;
    this.holdPending = true;

    this.twilioService
      .setCallHold(this.parentCallSid, nextHold)
      .pipe(take(1), takeUntil(this.holdCancel$))
      .subscribe({
        next: (result) => {
          this.isOnHold = result.hold;
          this.holdPending = false;
        },
        error: () => {
          this.holdPending = false;
          this.errorMessage = nextHold ? 'Failed to put the call on hold. Please try again.' : 'Failed to resume the call. Please try again.';
          setTimeout(() => (this.errorMessage = null), 5000);
        },
      });
  }

  private resolveCallerPatient(): void {
    // Prefer an explicit patient association carried on the call (provided by the backend via TwiML)
    // — more reliable than resolving by phone number.
    const injectedPatientId = this.data.call?.customParameters?.get('patientId');
    if (injectedPatientId) {
      this.matchedPatient = { userId: injectedPatientId, firstName: this.data.callerName ?? '', lastName: '' };
      return;
    }
    if (!this.data.callerNumber) {
      return;
    }
    this.patientLookupService
      .lookupByPhone(this.data.callerNumber)
      .pipe(take(1))
      .subscribe((patient) => (this.matchedPatient = patient));
  }

  sendDigits(digits: string): void {
    if (this.data.call) {
      this.data.call.sendDigits(digits);
    }
  }

  onKeypadToggled(opened: boolean): void {
    if (opened) {
      this.keypadPositionY = this.position.y;
      setTimeout(() => this.adjustDialogPositionToFitViewport());
    } else if (this.keypadPositionY !== null) {
      this.position.y = this.keypadPositionY;
      this.keypadPositionY = null;
      this.updateDialogPosition(true);
    }
  }

  toggleMute(): void {
    if (this.data.call) {
      this.isMuted = !this.isMuted;
      this.data.call.mute(this.isMuted);
    }
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;

    // Adjust position when minimizing to ensure it stays visible
    if (this.isMinimized) {
      // Move to bottom-left when minimized
      this.position.y = window.innerHeight - this.minimizedHeight - 50;
    } else {
      // Ensure full dialog fits when maximizing
      const maxY = window.innerHeight - this.dialogHeight - 100;
      this.position.y = Math.min(this.position.y, maxY);
    }

    this.updateDialogPosition();
  }

  startDragging(event: MouseEvent): void {
    this.isDragging = true;
    this.dragStart.x = event.clientX - this.position.x;
    this.dragStart.y = event.clientY - this.position.y;
    event.preventDefault();
    event.stopPropagation();
  }

  addParticipant(): void {
    this.isMinimized = false;
    this.showAddParticipant = false;

    const viewportHeight = window.innerHeight;
    const dialogBottom = this.position.y + this.dialogHeight;
    const maxVisibleY = viewportHeight - 20;

    if (dialogBottom > maxVisibleY) {
      const adjustment = dialogBottom - maxVisibleY;
      const additionalMargin = 20;
      this.position.y = Math.max(20, this.position.y - adjustment - additionalMargin);
      this.updateDialogPosition();
    }

    if (this.phoneNumberControl.valid) {
      const rawNumber = this.phoneNumberControl.value.replace(/\D/g, '');
      const formattedNumber = `+1${rawNumber}`;

      this.twilioService.addParticipantToCall(this.parentCallSid, formattedNumber, null).subscribe({
        next: (response) => {
          this.additionalParticipantNumber =
            '+1 ' + this.utilsService.formatPhoneNumberStringConferenceCalling(formattedNumber);
          this.phoneNumberControl.reset();
          this.additionalParticipantCallSid = response.data.addedParticipantCallSid;
          this.additionalParticipantSessionId = this.additionalParticipantCallSid;

          this.subscribeToParticipantCallLeg(this.additionalParticipantSessionId, this.additionalParticipantCallSid);
        },
        error: (error) => {
          console.error('Failed to add participant:', error);
          this.errorMessage = 'Failed to add participant. Please try again.';
          setTimeout(() => (this.errorMessage = null), 5000);
        },
      });

      this.phoneNumberControl.reset();
      this.showAddParticipant = false;
    }
  }

  formatPhoneNumber(control: UntypedFormControl): void {
    this.utilsService.formatPhoneNumberConferenceCalling(control);
  }

  toggleAddParticipant(): void {
    // Store original position when first showing participant input
    if (!this.showAddParticipant) {
      this.originalPositionY = this.position.y;
    }

    this.showAddParticipant = !this.showAddParticipant;

    if (this.showAddParticipant) {
      setTimeout(() => {
        this.adjustDialogPositionToFitViewport();
        const inputElement = document.querySelector('.participant-input input') as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 350);
    } else {
      // Return to original position
      this.position.y = this.originalPositionY;
      this.updateDialogPosition(true);
    }
  }

  hangUpParticipant(): void {
    if (this.additionalParticipantCallSid) {
      this.twilioService.hangUpParticipant(this.parentCallSid, this.additionalParticipantCallSid).subscribe({
        next: (response) => {
          this.cleanUpAdditionalParticipant();
        },
        error: (error) => {
          console.error('Failed to hang up participant:', error);
          this.errorMessage = 'Failed to hang up participant. Please try again.';
          setTimeout(() => (this.errorMessage = null), 5000);
        },
      });
    }
  }

  handleEnterKey(event: KeyboardEvent): void {
    if (this.phoneNumberControl.valid) {
      this.formatPhoneNumber(this.phoneNumberControl);
      this.addParticipant();
    }
  }

  cleanUpAdditionalParticipant(): void {
    this.additionalParticipantCallSid = null;
    this.additionalParticipantNumber = null;
    this.additionalParticipantSessionId = null;
  }

  private subscribeToParticipantCallLeg(sessionId: string, callSid: string): void {
    this.participantDestroy$ = new Subject<void>();

    this.twilioService
      .subscribeToParticipantCallLeg(sessionId, callSid)
      .pipe(takeUntil(this.participantDestroy$))
      .subscribe({
        next: (session) => {
          if (!session?.callLegs) {
            return;
          }

          const callLeg = session.callLegs[callSid];
          if (!callLeg) {
            return;
          }

          if (callLeg.status === 'completed') {
            this.removeParticipantFromUI();
            this.cleanupParticipantSubscription();
          }
        },
        error: (err) => {
          console.error('Firestore subscription error', err);
        },
      });
  }

  private removeParticipantFromUI(): void {
    this.additionalParticipantCallSid = null;
    this.additionalParticipantNumber = null;
  }

  private cleanupParticipantSubscription(): void {
    if (!this.participantDestroy$) {
      return;
    }
    this.participantDestroy$?.next();
    this.participantDestroy$?.complete();
    this.participantDestroy$ = undefined;
  }

  private adjustDialogPositionToFitViewport(): void {
    const padding = 20;
    const viewportHeight = window.innerHeight;
    const dialogElement = document.querySelector('.call-progress-container');
    if (!dialogElement) {
      return;
    }

    const dialogRect = dialogElement.getBoundingClientRect();
    const overflow = dialogRect.bottom - (viewportHeight - padding);

    if (overflow > 0) {
      const newY = Math.max(padding, this.position.y - overflow);
      if (newY !== this.position.y) {
        this.position.y = newY;
        this.updateDialogPosition(true);
      }
    }
  }

  private startTimer(): void {
    this.durationInterval = setInterval(() => {
      this.duration = this.duration + 1;
      this.durationFormatted = this.displayTime(this.duration);
    }, 1000);
  }

  private stopTimer(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
    }
  }

  private displayTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes?.toString().padStart(2, '0')}:${remainingSeconds?.toString().padStart(2, '0')}`;
  }

  private initializePosition(): void {
    // Position at bottom-left with some margin
    this.position.x = 20;
    this.position.y = window.innerHeight - (this.isMinimized ? this.minimizedHeight : this.dialogHeight) - 50;
    this.updateDialogPosition();
  }

  private updateDialogPosition(smooth = false): void {
    const transition = smooth ? 'transform 0.3s ease' : 'none';
    // Query for the dialog overlay pane (where panelClass is applied)
    const dialogContainer = document.querySelector('.call-in-progress-draggable') as HTMLElement;
    if (dialogContainer) {
      // Set position styles directly to avoid !important in CSS
      dialogContainer.style.position = 'fixed';
      dialogContainer.style.top = '0';
      dialogContainer.style.left = '0';
      dialogContainer.style.transform = `translate(${this.position.x}px, ${this.position.y}px)`;
      dialogContainer.style.transition = transition;
    } else {
      // Fallback: try to find it via cdk-overlay-pane
      const allPanes = Array.from(document.querySelectorAll('.cdk-overlay-pane'));
      const pane = allPanes.find((p) => p.classList.contains('call-in-progress-draggable')) as HTMLElement;
      if (pane) {
        pane.style.position = 'fixed';
        pane.style.top = '0';
        pane.style.left = '0';
        pane.style.transform = `translate(${this.position.x}px, ${this.position.y}px)`;
        pane.style.transition = transition;
      }
    }
  }

  /**
   * Surfaces a microphone that is not reaching the patient, from either of the two moments it can be
   * known: the check run just before the call was answered, and the SDK's own in-call warning when a
   * device dies mid-call.
   */
  private watchMicrophone(): void {
    const status = this.data.microphone?.status;
    if (status && isReportableMicrophoneFailure(status)) {
      this.microphoneWarning = MICROPHONE_CHECK_MESSAGES[status];
    }

    this.twilioService.audioWarnings$.pipe(takeUntil(this.destroy$)).subscribe((warning) => {
      if (clearsMicrophoneAlarm(warning)) {
        this.microphoneWarning = null;
      } else if (raisesMicrophoneAlarm(warning)) {
        this.microphoneWarning =
          'The caller is not hearing you — your microphone has stopped sending audio. Check your headset.';
      }
    });
  }

  private onCallEnded(): void {
    this.stopTimer();
    this.microphoneWarning = null;
    // Give the device back, so the next call re-acquires it rather than inheriting this call's track.
    this.twilioService.releaseMicrophone();
    this.resetHoldState();
    this.dialogRef.close({ action: 'ended', duration: this.duration });
  }

  /**
   * Clears the hold state so a finished call never leaves the button stuck showing "Resume".
   *
   * Also cancels any in-flight hold request, so a response arriving after the call ended cannot
   * set isOnHold back to true.
   */
  private resetHoldState(): void {
    this.holdCancel$.next();
    this.isOnHold = false;
    this.holdPending = false;
  }
}
